import { motion } from "framer-motion";
import { Reveal, Chapter, SectionHead } from "@/components/Reveal";
import { JoinCta } from "@/components/Buttons";
import {
  Route, CarFront, Signpost, Building2, Zap, CloudSun,
} from "lucide-react";

const ROAD_IMG =
  "https://images.unsplash.com/photo-1625556841726-ee3959350033?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxhdXRvbm9tb3VzJTIwZHJpdmluZyUyMHZpZXclMjBjaXR5JTIwc3RyZWV0fGVufDB8fHx8MTc4OTQ1ODQwOXww&ixlib=rb-4.1.0&q=85";

const OVERLAYS = [
  { label: "Pothole · 97.4%", cls: "border-signal-coral text-signal-coral", pos: "left-[14%] bottom-[18%] w-[26%] h-[16%]", delay: 0.7 },
  { label: "Traffic sign", cls: "border-signal-green text-signal-green", pos: "right-[16%] top-[16%] w-[9%] h-[20%]", delay: 1.2 },
  { label: "Vehicle", cls: "border-signal-violet text-signal-violet", pos: "left-[42%] top-[38%] w-[14%] h-[18%]", delay: 1.7 },
  { label: "Lane marking", cls: "border-signal-cyan text-signal-cyan", pos: "left-[10%] top-[52%] w-[18%] h-[10%]", delay: 2.2 },
  { label: "Road damage", cls: "border-amber-400 text-amber-400", pos: "right-[24%] bottom-[14%] w-[20%] h-[12%]", delay: 2.7 },
];

const DATA_TYPES = [
  { icon: Route, name: "Road condition", text: "Surface quality and change over time" },
  { icon: CarFront, name: "Vehicle context", text: "Motion and vehicle signals" },
  { icon: Signpost, name: "Traffic infrastructure", text: "Signs, signals and markings" },
  { icon: Building2, name: "Building condition", text: "Physical state of structures" },
  { icon: Zap, name: "Electrical systems", text: "Grid and utility observations" },
  { icon: CloudSun, name: "Environmental signals", text: "Weather and ambient context" },
];

export const RoadIntelligence = () => {
  return (
    <section id="roads" className="relative py-28 sm:py-40 bg-ink-900/40" data-testid="section-roads">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <Chapter label="Road intelligence" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <SectionHead title={"Synaphy sees\nwhat changes."} />
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-lg leading-relaxed text-steel max-w-md">
              Maps tell you where things are. Synaphy tells you what changed -
              captured by contributors, understood by AI, verified by the
              network.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="relative mt-16 overflow-hidden rounded-2xl" data-testid="road-visual">
            <img
              src={ROAD_IMG}
              alt="City street observed by a contributor device"
              className="h-[380px] sm:h-[520px] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950/80 via-ink-950/10 to-ink-950/30" />
            <div className="absolute left-0 right-0 h-px bg-signal-cyan/40 scan-line" aria-hidden="true" />

            {OVERLAYS.map((o) => (
              <motion.div
                key={o.label}
                initial={{ opacity: 0, scale: 0.94 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: o.delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className={`absolute rounded border ${o.cls} ${o.pos}`}
                data-testid={`road-overlay-${o.label.toLowerCase().replace(/[\s·.%]+/g, "-")}`}
              >
                <span className={`absolute -top-6 left-0 text-[11px] font-medium whitespace-nowrap ${o.cls.split(" ")[1]}`}>
                  {o.label}
                </span>
                <span className="absolute left-0 top-0 h-1.5 w-1.5 border-l border-t border-current" />
                <span className="absolute right-0 bottom-0 h-1.5 w-1.5 border-r border-b border-current" />
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 3.1, duration: 0.8 }}
              className="absolute bottom-4 left-4 right-4 sm:right-auto flex items-center gap-3 font-mono text-[9px] sm:text-[10px] tracking-[0.1em] text-zinc-400"
              data-testid="road-structured-readout"
            >
              <span className="text-steel">observation</span>
              <span className="text-signal-cyan">→</span>
              <span className="text-mist/80">{`{ object: "pothole", condition: "severe", confidence: 0.974, verified: true }`}</span>
            </motion.div>
            <div className="absolute top-4 right-4 font-mono text-[9px] tracking-[0.18em] text-zinc-500 uppercase">
              Concept visualization
            </div>
          </div>
        </Reveal>

        <div className="mt-24 sm:mt-32">
          <Chapter label="Physical world data" />
          <SectionHead
            title={"Structured data,\nready for machines."}
            className="max-w-2xl"
          />
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10">
            {DATA_TYPES.map((d, i) => (
              <Reveal key={d.name} delay={0.06 * i} y={14}>
                <div
                  className={`flex items-start gap-5 py-7 ${i > 2 ? "lg:border-t lg:border-white/5" : ""} ${i % 3 !== 0 ? "" : ""} ${i > 0 ? "border-t border-white/5 lg:border-t" : "border-t border-white/5 lg:border-t"}`}
                  data-testid={`data-item-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <d.icon className="h-6 w-6 text-signal-cyan shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display text-lg font-semibold tracking-tight text-mist">{d.name}</h3>
                    <p className="mt-1.5 text-[15px] leading-relaxed text-steel">{d.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25}>
            <p className="mt-8 text-sm text-steel/80">
              Designed data layers. Not every source is live today - future
              capabilities are built through the Pilot phase.
            </p>
            <div className="mt-10">
              <JoinCta testid="roads-join-btn">Join the road intelligence network</JoinCta>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
